/**
 * The file kinds a layout's media component can accept, as the API's mime type
 * enum names. Grouped the way layout authors pick them: images, HTML, video and
 * audio as a whole, and the rest one kind at a time.
 */

export type MediaGroup = 'image' | 'html' | 'video' | 'audio' | 'other'

export const MEDIA_GROUPS: MediaGroup[] = ['image', 'html', 'video', 'audio', 'other']

interface MediaKind {
  id: string
  group: MediaGroup
  mimeTypes: string[]
}

export const MEDIA_KINDS: MediaKind[] = [
  { id: 'gif', group: 'image', mimeTypes: ['IMAGE_GIF'] },
  { id: 'jpg', group: 'image', mimeTypes: ['IMAGE_JPEG', 'IMAGE_PJPEG'] },
  { id: 'png', group: 'image', mimeTypes: ['IMAGE_PNG', 'IMAGE_X_PNG'] },
  { id: 'svg', group: 'image', mimeTypes: ['IMAGE_SVG'] },
  { id: 'webp', group: 'image', mimeTypes: ['IMAGE_WEBP'] },
  { id: 'html', group: 'html', mimeTypes: ['TEXT_HTML', 'TEXT_XHTML'] },
  { id: 'mp4', group: 'video', mimeTypes: ['VIDEO_MP4', 'VIDEO_M4V', 'VIDEO_X_M4V'] },
  { id: 'webm', group: 'video', mimeTypes: ['VIDEO_WEBM'] },
  { id: 'quicktime', group: 'video', mimeTypes: ['VIDEO_QUICKTIME'] },
  { id: 'hls', group: 'video', mimeTypes: ['VIDEO_HLS'] },
  { id: 'dash', group: 'video', mimeTypes: ['VIDEO_DASH'] },
  { id: 'mp3', group: 'audio', mimeTypes: ['AUDIO_MP3'] },
  {
    id: 'font',
    group: 'other',
    mimeTypes: [
      'APPLICATION_X_WOFF',
      'APPLICATION_X_FONT_TTF',
      'APPLICATION_X_FONT_WOFF',
      'APPLICATION_MS_FONT_OBJECT',
      'APPLICATION_FONT_WOFF',
      'APPLICATION_X_FONT_OTF',
    ],
  },
  { id: 'css', group: 'other', mimeTypes: ['TEXT_CSS'] },
  { id: 'csv', group: 'other', mimeTypes: ['TEXT_CSV'] },
  { id: 'excel', group: 'other', mimeTypes: ['APPLICATION_XLS', 'APPLICATION_XLSX'] },
  { id: 'js', group: 'other', mimeTypes: ['APPLICATION_JAVASCRIPT', 'TEXT_JAVASCRIPT'] },
  { id: 'subtitle', group: 'other', mimeTypes: ['TEXT_VTT', 'TEXT_SRT'] },
  { id: 'json', group: 'other', mimeTypes: ['APPLICATION_JSON'] },
  { id: 'txt', group: 'other', mimeTypes: ['TEXT_PLAIN'] },
]

export const OTHER_MEDIA_KINDS = MEDIA_KINDS.filter((kind) => kind.group === 'other')

/** Every mime type of a group, or of one kind when the group is "other". */
export function mimeTypesFor(group: MediaGroup, otherKind?: string): string[] {
  if (group === 'other') {
    return MEDIA_KINDS.find((kind) => kind.id === otherKind)?.mimeTypes ?? []
  }
  return MEDIA_KINDS.filter((kind) => kind.group === group).flatMap((kind) => kind.mimeTypes)
}

/** The kinds a list of mime types covers, e.g. ["jpg", "png"]. */
export function mediaKindsOf(mimeTypes: string[] = []): MediaKind[] {
  return MEDIA_KINDS.filter((kind) => kind.mimeTypes.some((m) => mimeTypes.includes(m)))
}

/** Reads back the group (and the "other" kind) a saved component was set up with. */
export function mediaGroupOf(mimeTypes: string[] = []): { group: MediaGroup; otherKind?: string } {
  const first = mediaKindsOf(mimeTypes)[0]
  if (!first) return { group: mimeTypes.length ? 'other' : 'image' }
  return first.group === 'other' ? { group: 'other', otherKind: first.id } : { group: first.group }
}
